// Direct WebSocket text / voice field group for the interface editor drawer.
// Voice codec and sample-rate choices come only from the active capability
// projected by GET /v1/me; the web never duplicates the capability matrix.
import type { components } from "../../../api/schema";
import { HelpTip, Select, Stack, TextInput, Textarea } from "../../../ui";
import type { InterfaceFieldsProps } from "../lib/connectionDraft";

type ConnectionCapability =
  components["schemas"]["ConnectionCapabilityResponse"];
type TelephonyCodec = components["schemas"]["TelephonyCodec"];
type WebsocketAudioEnvelope = components["schemas"]["WebsocketAudioEnvelope"];
type WebsocketAuthType = components["schemas"]["WebsocketAuthType"];

interface WebsocketInterfaceFieldsProps extends InterfaceFieldsProps {
  voiceCapability: ConnectionCapability | undefined;
}

export function WebsocketInterfaceFields({
  draft,
  onDraftChange,
  voiceCapability,
}: WebsocketInterfaceFieldsProps) {
  const isVoice = draft.interfaceKind === "websocket_voice";
  const codecs = voiceCapability?.codecs ?? [];
  const sampleRatesHz = voiceCapability?.sample_rates_hz ?? [];
  return (
    <Stack gap={4}>
      <TextInput
        label="WebSocket URL"
        required
        type="text"
        value={draft.websocketUrl}
        onChange={(event) => {
          onDraftChange({
            ...draft,
            websocketUrl: event.currentTarget.value,
          });
        }}
      />
      <Select
        label="Authentication"
        help={
          <HelpTip label="About WebSocket authentication">
            Choose the authentication method required by your endpoint.
            Authenticated connections use a secret stored in your deployment's
            secret manager.
          </HelpTip>
        }
        required
        value={draft.websocketAuthType}
        onChange={(event) => {
          onDraftChange({
            ...draft,
            websocketAuthType: event.currentTarget.value as WebsocketAuthType,
          });
        }}
      >
        <option value="none">No authentication</option>
        <option value="bearer">Bearer token</option>
        <option value="header">Authentication header</option>
      </Select>
      <TextInput
        label="Secret reference"
        help={
          <HelpTip label="About the authentication secret">
            Enter the reference supplied by your deployment administrator, not
            the secret itself. To replace the secret value, update it in your
            deployment's secret manager. This field changes which stored secret
            the interface uses.
          </HelpTip>
        }
        value={draft.websocketSecretRef}
        onChange={(event) => {
          onDraftChange({
            ...draft,
            websocketSecretRef: event.currentTarget.value,
          });
        }}
      />
      <Textarea
        help={
          <HelpTip label="About WebSocket headers">
            Enter a JSON object of non-secret headers. Configure authentication
            using the secret reference; do not paste tokens or API keys here.
          </HelpTip>
        }
        label="Headers"
        rows={5}
        value={draft.websocketHeaders}
        onChange={(event) => {
          onDraftChange({
            ...draft,
            websocketHeaders: event.currentTarget.value,
          });
        }}
      />
      {isVoice ? (
        <>
          <Select
            label="Audio envelope"
            required
            value={draft.websocketEnvelope}
            onChange={(event) => {
              onDraftChange({
                ...draft,
                websocketEnvelope: event.currentTarget
                  .value as WebsocketAudioEnvelope,
              });
            }}
          >
            <option value="">Select an envelope</option>
            <option value="raw_pcm">raw PCM</option>
            <option value="base64_json">base64 JSON</option>
          </Select>
          <Select
            label="Audio codec"
            required
            value={draft.websocketCodec}
            onChange={(event) => {
              onDraftChange({
                ...draft,
                websocketCodec: event.currentTarget.value as TelephonyCodec,
              });
            }}
          >
            <option value="">Select a codec</option>
            {codecs.map((codec) => (
              <option key={codec} value={codec}>
                {codec}
              </option>
            ))}
          </Select>
          <Select
            label="Audio sample rate"
            required
            value={draft.websocketSampleRateHz}
            onChange={(event) => {
              onDraftChange({
                ...draft,
                websocketSampleRateHz: event.currentTarget.value,
              });
            }}
          >
            <option value="">Select a sample rate</option>
            {sampleRatesHz.map((sampleRate) => (
              <option key={sampleRate} value={String(sampleRate)}>
                {sampleRate} Hz
              </option>
            ))}
          </Select>
        </>
      ) : null}
    </Stack>
  );
}
